import { createI18n, type I18n } from 'vue-i18n'
import {
  DEFAULT_LOCALE,
  FALLBACK_LOCALE,
  STORAGE_KEY,
  SUPPORTED_LOCALES,
  LOCALE_META,
  detectInitialLocale,
  normalizeLocale,
  readStoredLocale,
  storeLocale,
  type SupportedLocale,
} from './detect'
import ptBR from './locales/pt-BR'
import { missingHandler } from './missing'

export { getMissingKeys } from './missing'
export {
  DEFAULT_LOCALE,
  FALLBACK_LOCALE,
  STORAGE_KEY,
  SUPPORTED_LOCALES,
  LOCALE_META,
  normalizeLocale,
  readStoredLocale,
  storeLocale,
  detectInitialLocale,
}
export type { SupportedLocale }
export * from './format'

/** Locale atualmente ativo — lido pelos formatters de `packages/features`. */
let activeLocale: SupportedLocale | null = null

export function currentLocale(): SupportedLocale {
  return activeLocale ?? readStoredLocale() ?? detectInitialLocale()
}

export function hasStoredLocale(): boolean {
  return Boolean(readStoredLocale())
}

export type PersistFn = (locale: SupportedLocale) => void | Promise<unknown>

const loaders: Record<SupportedLocale, () => Promise<{ default: Record<string, unknown> }>> = {
  'pt-BR': () => Promise.resolve({ default: ptBR }),
  en: () => import('./locales/en'),
  es: () => import('./locales/es'),
}

/** Superfície mínima usada — compatível com I18n e com o Composer de `useI18n()`. */
export interface LocaleComposer {
  locale: { value: string }
  availableLocales: string[]
  setLocaleMessage(locale: string, messages: Record<string, unknown>): void
  t(key: string, named?: Record<string, unknown>): string
}

export type I18nLike = I18n | LocaleComposer | { global: LocaleComposer }

function syncDocumentLocale(locale: SupportedLocale): void {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale
  }
}

/** Aceita a instância I18n ou o Composer global retornado por `useI18n()`. */
function composerOf(i18n: I18nLike): LocaleComposer {
  return 'global' in i18n ? i18n.global as LocaleComposer : i18n as LocaleComposer
}

function readLocale(composer: LocaleComposer): string {
  return composer.locale.value
}

/**
 * Troca o locale ativo: lazy-load do pack, aplica, persiste local
 * e (opcionalmente) propaga para o perfil do usuário autenticado.
 */
export async function setLocale(i18n: I18nLike, locale: string, persist?: PersistFn): Promise<SupportedLocale> {
  const normalized = normalizeLocale(locale) ?? DEFAULT_LOCALE
  const composer = composerOf(i18n)

  if (!composer.availableLocales.includes(normalized)) {
    const mod = await loaders[normalized]()
    composer.setLocaleMessage(normalized, mod.default)
  }

  composer.locale.value = normalized
  storeLocale(normalized)
  activeLocale = normalized
  syncDocumentLocale(normalized)
  await persist?.(normalized)
  return normalized
}

/**
 * Após login/`me`: a preferência do perfil vence o storage local.
 * Sem preferência válida, devolve o locale atual para o perfil persistir.
 */
export async function applyUserLocale(
  i18n: I18nLike,
  userLocale: string | null | undefined,
  persist?: PersistFn,
): Promise<SupportedLocale> {
  const fromProfile = normalizeLocale(userLocale)
  const current = readLocale(composerOf(i18n)) as SupportedLocale

  if (fromProfile && fromProfile !== current) {
    return setLocale(i18n, fromProfile)
  }
  if (!fromProfile && current !== DEFAULT_LOCALE) {
    await persist?.(current)
  }
  return current
}

export interface LocziI18nOptions {
  /** Chamado quando o usuário troca o idioma autenticado — ex.: PATCH /me/locale. */
  persist?: PersistFn
}

export interface LocziI18n extends I18n {
  /** Atalho registrado na criação — troca o locale e persiste (req. 8/9). */
  locziSetLocale(locale: string): Promise<SupportedLocale>
}

export function createLocziI18n(options: LocziI18nOptions = {}): LocziI18n {
  const initial = detectInitialLocale()

  const i18n = createI18n({
    legacy: false,
    globalInjection: true,
    locale: initial,
    fallbackLocale: FALLBACK_LOCALE,
    fallbackWarn: false,
    missingWarn: false,
    missing: missingHandler,
    messages: { 'pt-BR': ptBR },
  })

  activeLocale = initial
  syncDocumentLocale(initial)
  if (initial !== DEFAULT_LOCALE) {
    void setLocale(i18n, initial)
  }

  const out = i18n as unknown as LocziI18n
  out.locziSetLocale = (locale: string) => setLocale(i18n, locale, options.persist)
  return out
}
